import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import './HomePageV3.css';
import { useNavigate } from 'react-router-dom';

function HomePageV4() {
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="homepage-v3">
      {/* Navigation */}
      <nav className={`nav ${scrolled ? 'nav-scrolled' : ''}`}>
        <div className="nav-container">
          <a href="/" className="nav-logo" aria-label="CerebralMachines home"><img src="/CMLogo.png" alt="CerebralMachines" /></a>
          <div className="nav-links">
            <a href="/">Home</a>
            <a href="/platform">Platform</a>
            <a href="/how-it-works">How it Works</a>
            <a href="/use-cases">Use Cases</a>
            <a href="#docs">Docs</a>
            <a href="#blog">Blog</a>
          </div>
          <div className="nav-actions">
            <button className="btn-ghost" onClick={() => navigate('/login')}>Sign In</button>
            <button className="btn-primary">Request a demo</button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-container">
          <motion.div 
            className="hero-left"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="eyebrow">Hybrid Cognitive Linguistic Core (HCLC)</div>
            <h1 className="hero-title">The Cognitive Brain for Your Enterprise.</h1>
            <p className="hero-subtitle">
              The first AI system engineered for deterministic reasoning, zero hallucination, real-time knowledge integration, and full causal explainability.
            </p>
            
            <div className="hero-pills">
              <div className="pill">
                <span className="pill-icon">✗</span>
                No RAG
              </div>
              <div className="pill">
                <span className="pill-icon">✗</span>
                No fine-tuning
              </div>
              <div className="pill">
                <span className="pill-icon">✗</span>
                No black boxes
              </div>
            </div>

            <p className="hero-subtitle" style={{ marginTop: '1.5rem', fontWeight: '600', fontSize: '1.25rem' }}>
              AI that thinks before it answers.
            </p>

            <div className="hero-cta">
              <button className="btn-primary-large" onClick={() => navigate('/signup')}>→ Explore the Cognitive Core</button>
              <button className="btn-secondary-large" onClick={() => navigate('/signup')}>→ Book a Live Demo</button>
            </div>
          </motion.div>

          <motion.div 
            className="hero-right"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="hero-usps-card">
              <ul className="usps-list">
                <li>
                  <span className="usp-icon">✓</span>
                  <span className="usp-text">RAG replaced.</span>
                </li>
                <li>
                  <span className="usp-icon">✓</span>
                  <span className="usp-text">No fine-tuning.</span>
                </li>
                <li>
                  <span className="usp-icon">✓</span>
                  <span className="usp-text">Zero hallucination.</span>
                </li>
                <li>
                  <span className="usp-icon">✓</span>
                  <span className="usp-text">Deterministic reasoning.</span>
                </li>
                <li>
                  <span className="usp-icon">✓</span>
                  <span className="usp-text">Full explainability.</span>
                </li>
                <li>
                  <span className="usp-icon">✓</span>
                  <span className="usp-text">Human-like context, cross-domain.</span>
                </li>
                <li>
                  <span className="usp-icon">✓</span>
                  <span className="usp-text">Real-time knowledge integration.</span>
                </li>
                <li>
                  <span className="usp-icon">✓</span>
                  <span className="usp-text">GPU-optional, ultra-low cost.</span>
                </li>
                <li>
                  <span className="usp-icon">✓</span>
                  <span className="usp-text">Complete behavioral control.</span>
                </li>
                <li>
                  <span className="usp-icon">✓</span>
                  <span className="usp-text">Enterprise-safe, easily auditable.</span>
                </li>
              </ul>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Section 1: The Gap */}
      <section className="gap-section">
        <div className="section-container">
          <div className="section-header-centered">
            <div className="eyebrow-dark">THE GAP</div>
            <h2 className="section-title-dark">LLMs Changed Everything. Except What Enterprises Need Most.</h2>
            <p className="section-subtitle-dark">
              Large Language Models are powerful. But between that power and enterprise-grade deployment lies a foundational gap no amount of prompt engineering, RAG, or fine-tuning can close.
            </p>
          </div>

          <div className="gap-grid">
            {[
              {
                icon: (
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                    <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
                    <line x1="12" y1="22.08" x2="12" y2="12"/>
                  </svg>
                ),
                title: "Knowledge Integration at Scale",
                text: "Enterprises sit on vast, interconnected knowledge ecosystems \u2014 policies, regulations, clinical guidelines, cross-domain dependencies. Current approaches fall short of representing this complexity with the fidelity enterprises demand."
              },
              {
                icon: (
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                    <line x1="12" y1="9" x2="12" y2="13"/>
                    <line x1="12" y1="17" x2="12.01" y2="17"/>
                  </svg>
                ),
                title: "Hallucination in High-Stakes Decisions",
                text: "When AI fabricates an answer in healthcare, finance, or compliance, the consequences are real. Enterprises need guarantees \u2014 not probabilities."
              },
              {
                icon: (
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <circle cx="11" cy="11" r="8"/>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                    <line x1="8" y1="11" x2="14" y2="11"/>
                  </svg>
                ),
                title: "The Explainability Deficit",
                text: "Regulators and auditors demand to know why a decision was made. Today\u2019s AI can produce an answer, but not a traceable, causal explanation that satisfies an audit."
              },
              {
                icon: (
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
                  </svg>
                ),
                title: "No Deterministic Control",
                text: "Same question, different answer. Enterprises require deterministic behavior \u2014 reproducible outputs, inspectable reasoning, and the ability to govern AI decisions with precision."
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                className="gap-card"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="gap-card-icon">{item.icon}</div>
                <h3 className="gap-card-title">{item.title}</h3>
                <p className="gap-card-text">{item.text}</p>
              </motion.div>
            ))}
          </div>

          <motion.p
            className="gap-closing"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            Enterprises don't need more AI capabilities. They need the foundation to make AI truly enterprise-ready.
          </motion.p>
        </div>
      </section>

      {/* Section 2: The Solution */}
      <section className="solution-section">
        <div className="section-container">
          <div className="section-header-centered">
            <div className="eyebrow-light">THE SOLUTION</div>
            <h2 className="section-title-light">Introducing HCLC &mdash; A New Category of Enterprise Intelligence</h2>
            <p className="section-subtitle-light">
              A groundbreaking foundational architecture innovation that bridges the gap between LLM power and enterprise requirements &mdash; delivering determinism, deep reasoning, explainability, and precision control the industry has been missing.
            </p>
          </div>

          <div className="solution-pillars">
            {[
              {
                icon: (
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                    <path d="M2 17l10 5 10-5"/>
                    <path d="M2 12l10 5 10-5"/>
                  </svg>
                ),
                title: "Deep Knowledge Integration",
                text: "Represent and reason over vast, complex enterprise knowledge \u2014 cross-domain dependencies, multi-layered policies, and nuanced context that current approaches simply can\u2019t handle."
              },
              {
                icon: (
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                    <polyline points="9 12 11 14 15 10"/>
                  </svg>
                ),
                title: "Deterministic Precision",
                text: "Eliminate hallucination. Every output is grounded in evidence, reproducible across runs, and traceable to its source \u2014 the reliability regulated enterprises demand."
              },
              {
                icon: (
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M12 16v-4"/>
                    <path d="M12 8h.01"/>
                  </svg>
                ),
                title: "Complete Explainability & Control",
                text: "Every decision comes with a full causal reasoning trail. Inspect, tune, and govern AI behavior at the finest level of granularity."
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                className="solution-pillar-card"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
              >
                <div className="solution-pillar-icon">{item.icon}</div>
                <h3 className="solution-pillar-title">{item.title}</h3>
                <p className="solution-pillar-text">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 3: Core Capabilities */}
      <section className="capabilities-section">
        <div className="section-container">
          <div className="section-header-centered">
            <div className="eyebrow-dark">CORE CAPABILITIES</div>
            <h2 className="section-title-dark">What HCLC Unlocks for the Enterprise</h2>
          </div>

          <div className="capabilities-grid">
            {[
              {
                icon: (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                    <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
                    <line x1="12" y1="22.08" x2="12" y2="12"/>
                  </svg>
                ),
                title: "Enterprise Knowledge at Full Fidelity",
                text: "Integrate vast document ecosystems, complex regulatory frameworks, and cross-domain dependencies into a unified intelligence layer \u2014 preserving the nuance and interconnections that simpler approaches lose."
              },
              {
                icon: (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                  </svg>
                ),
                title: "Zero Hallucination",
                text: "Every answer is grounded in your actual data through deterministic reasoning. If the evidence doesn\u2019t support a conclusion, the system tells you \u2014 instead of making one up."
              },
              {
                icon: (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                ),
                title: "Causal Explainability",
                text: "Trace every decision back through its complete reasoning chain. See exactly which evidence, rules, and connections led to each output \u2014 audit-ready by default."
              },
              {
                icon: (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
                  </svg>
                ),
                title: "Real-Time Knowledge Updates",
                text: "New documents, updated policies, changing regulations \u2014 knowledge flows into the system and becomes immediately available. No retraining. No pipeline delays. No downtime."
              },
              {
                icon: (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                    <line x1="8" y1="21" x2="16" y2="21"/>
                    <line x1="12" y1="17" x2="12" y2="21"/>
                  </svg>
                ),
                title: "Radical Infrastructure Efficiency",
                text: "Run production workloads without expensive GPU clusters. Dramatic cost reduction with no compromise on reasoning depth or accuracy."
              },
              {
                icon: (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <circle cx="12" cy="12" r="3"/>
                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                  </svg>
                ),
                title: "Precision Behavioral Control",
                text: "Inspect and adjust AI reasoning at the finest level of granularity. Define guardrails, enforce policies, and debug with surgical precision \u2014 impossible with black-box models."
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                className="capability-card"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
              >
                <div className="capability-icon">{item.icon}</div>
                <h3 className="capability-title">{item.title}</h3>
                <p className="capability-text">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 4: The Result */}
      <section className="result-section">
        <div className="section-container">
          <div className="section-header-centered">
            <div className="eyebrow-light">THE RESULT</div>
            <h2 className="section-title-light">The Foundation for Real Enterprise AI</h2>
          </div>

          <div className="result-pillars">
            {[
              {
                icon: (
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                    <polyline points="22 4 12 14.01 9 11.01"/>
                  </svg>
                ),
                title: "Unlock Advanced Use Cases That Deliver ROI",
                text: "Move beyond simple Q&A into complex multi-step reasoning, cross-domain analysis, and high-stakes decision automation \u2014 the use cases that actually transform operations."
              },
              {
                icon: (
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                  </svg>
                ),
                title: "Compliant and Auditable by Design",
                text: "Full audit trails, deterministic outputs, and traceable reasoning chains. Built for regulated industries from day one."
              },
              {
                icon: (
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <line x1="12" y1="20" x2="12" y2="10"/>
                    <line x1="18" y1="20" x2="18" y2="4"/>
                    <line x1="6" y1="20" x2="6" y2="16"/>
                  </svg>
                ),
                title: "From Pilot Purgatory to Production",
                text: "No more proof-of-concept graveyards. HCLC delivers the reliability, control, and explainability enterprises need to put AI into real production."
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                className="result-card"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
              >
                <div className="result-card-icon">{item.icon}</div>
                <h3 className="result-card-title">{item.title}</h3>
                <p className="result-card-text">{item.text}</p>
              </motion.div>
            ))}
          </div>

          <motion.p
            className="result-accent"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            Control isn't a feature. It's the foundation.
          </motion.p>
        </div>
      </section>

      {/* Section 5: Use Cases */}
      <section className="usecases-section">
        <div className="section-container">
          <div className="section-header-centered">
            <div className="eyebrow-dark">IN PRACTICE</div>
            <h2 className="section-title-dark">Where Enterprise AI Finally Delivers</h2>
            <p className="section-subtitle-dark">
              HCLC powers the advanced AI use cases enterprises have been waiting for &mdash; complex reasoning, deterministic decisions, and full explainability across every industry.
            </p>
          </div>

          <div className="usecases-grid">
            {[
              {
                icon: (
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
                  </svg>
                ),
                title: "Healthcare",
                subtitle: "Prior Authorization & Compliance",
                text: "Automate medical necessity determinations, prior authorization, and compliance audits with deterministic precision. Every decision is explainable, audit-ready, and grounded in clinical evidence and payer policies.",
                callout: "From weeks of manual review to instant, explainable decisions."
              },
              {
                icon: (
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <line x1="12" y1="1" x2="12" y2="23"/>
                    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                  </svg>
                ),
                title: "Financial Services",
                subtitle: "Risk & Regulatory Compliance",
                text: "Complex risk scoring, fraud pattern analysis, and regulatory compliance \u2014 with complete reasoning trails. Every output traces back to the evidence your regulators can verify.",
                callout: "Explainable decisions that satisfy both regulators and risk officers."
              },
              {
                icon: (
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                  </svg>
                ),
                title: "Insurance",
                subtitle: "Claims & Underwriting",
                text: "Claims adjudication, eligibility computation, and policy enforcement with deterministic accuracy. Full reasoning trails accelerate dispute resolution and ensure consistency across millions of decisions.",
                callout: "Policy logic applied consistently, explainably, at scale."
              },
              {
                icon: (
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                    <line x1="16" y1="13" x2="8" y2="13"/>
                    <line x1="16" y1="17" x2="8" y2="17"/>
                    <polyline points="10 9 9 9 8 9"/>
                  </svg>
                ),
                title: "Legal & Enterprise Operations",
                subtitle: "Document Intelligence",
                text: "Extract obligations, deadlines, and dependencies from complex document ecosystems. Enable cross-reference queries across contracts, regulations, and internal policies \u2014 with complete traceability.",
                callout: "From document complexity to operational intelligence."
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                className="usecase-card"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.12 }}
              >
                <div className="usecase-icon">{item.icon}</div>
                <div className="usecase-content">
                  <h3 className="usecase-title">{item.title}</h3>
                  <p className="usecase-subtitle">{item.subtitle}</p>
                  <p className="usecase-text">{item.text}</p>
                  <p className="usecase-callout">{item.callout}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 6: Final CTA */}
      <section className="cta-strip">
        <div className="cta-container">
          <motion.div
            className="cta-content"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="cta-title">Your Enterprise AI Journey Starts Here.</h2>
            <p className="cta-subtitle">
              CerebralMachines delivers the foundational intelligence layer your enterprise needs &mdash; deterministic, explainable, and built for production.
            </p>
            <div className="cta-buttons">
              <button className="btn-primary-large" onClick={() => navigate('/signup')}>Request Early Access</button>
              <button className="btn-secondary-large" onClick={() => navigate('/signup')}>Talk to an Architect</button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-container">
          <div className="footer-main">
            <div className="footer-brand">
              <a href="/" className="footer-logo" aria-label="CerebralMachines home"><img src="/CMLogo.png" alt="CerebralMachines" /></a>
              <p className="footer-tagline">
                The foundational intelligence platform for the enterprise.
              </p>
              <div className="footer-social">
                <a href="#" aria-label="LinkedIn">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/>
                    <rect x="2" y="9" width="4" height="12"/>
                    <circle cx="4" cy="4" r="2"/>
                  </svg>
                </a>
                <a href="#" aria-label="Twitter/X">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z"/>
                  </svg>
                </a>
                <a href="#" aria-label="GitHub">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22"/>
                  </svg>
                </a>
              </div>
            </div>

            <div className="footer-links">
              <div className="footer-column">
                <h4 className="footer-heading">Product</h4>
                <ul>
                  <li><a href="#">Platform</a></li>
                  <li><a href="#">How it Works</a></li>
                  <li><a href="#">Use Cases</a></li>
                  <li><a href="#">Docs</a></li>
                  <li><a href="#">API</a></li>
                </ul>
              </div>

              <div className="footer-column">
                <h4 className="footer-heading">Company</h4>
                <ul>
                  <li><a href="#">About</a></li>
                  <li><a href="#">Team</a></li>
                  <li><a href="#">Careers</a></li>
                  <li><a href="#">Contact</a></li>
                </ul>
              </div>

              <div className="footer-column">
                <h4 className="footer-heading">Resources</h4>
                <ul>
                  <li><a href="#">Blog</a></li>
                  <li><a href="#">Guides</a></li>
                  <li><a href="#">Events</a></li>
                  <li><a href="#">Community</a></li>
                </ul>
              </div>

              <div className="footer-column">
                <h4 className="footer-heading">Legal</h4>
                <ul>
                  <li><a href="#">Privacy Policy</a></li>
                  <li><a href="#">Terms of Service</a></li>
                  <li><a href="#">Security</a></li>
                </ul>
              </div>
            </div>
          </div>

          <div className="footer-bottom">
            <p>&copy; 2026 CerebralMachines. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default HomePageV4;

